import mitt, { type Emitter, type Handler } from "mitt";

import type { RetrieveParameters } from "./DataHandler.js";

/**
 * Events that can be triggered by a data object
 */
export type DataObjectEventToValueMap<T> = {
	onCurrentIndexChanging: number;
	onCurrentIndexChanged: number;
	onMasterIndexChanging: number;
	onAllowDeleteChanged: boolean;
	onAllowUpdateChanged: boolean;
	onAllowInsertChanged: boolean;
	onBeginEdit: number;
	onCancelEdit: number;
	onBeforeSave: Partial<T>;
	onSaving: undefined;
	onAfterSave: number;
	onSaveFailed: number;
	onBeforeLoad: RetrieveParameters<T>;
	onDataLoaded: number;
	onDataLoading: null;
	onMasterBeforeLoad: null;
	onPartialDataLoaded: { skip: number; count: number; total: number };
	onDataLoadFailed: string | null;
	onDirtyChanged: boolean;
	onFieldChanged: { name: keyof T; value: T[keyof T] };
	onParameterUpdated: ParameterUpdatedValue<T, keyof RetrieveParameters<T>>;
	onRecordCreated: number;
	onRecordRefreshed: number;
	onRecordDeleting: number;
	onRecordDeleted: number;
	onRecordDeleted2: { index: number; uid: any };
	onRowCountLoaded: { skip: number; count: number; total: number };
	onClearFilter: undefined;
};

export type EventOptions = {
	/**
	 * If true, the handler will only be called once, before it is removed.
	 */
	once?: boolean;
	/**
	 * If you pass an AbortSignal to the event options, the handler will be detached when the signal is aborted.
	 */
	signal?: AbortSignal;
};

export type ParameterUpdatedValue<T, K extends keyof RetrieveParameters<T>> = {
	name: K;
	value: RetrieveParameters<T>[K];
};

export type DataObjectEvent = keyof DataObjectEventToValueMap<any>;

export const DataObjectEvents: Record<DataObjectEvent, { abortable: boolean }> = {
	onCurrentIndexChanging: { abortable: true },
	onCurrentIndexChanged: { abortable: false },
	onMasterIndexChanging: { abortable: true },
	onAllowDeleteChanged: { abortable: false },
	onAllowUpdateChanged: { abortable: false },
	onAllowInsertChanged: { abortable: false },
	onBeginEdit: { abortable: true },
	onCancelEdit: { abortable: true },
	onBeforeSave: { abortable: true },
	onSaving: { abortable: false },
	onAfterSave: { abortable: false },
	onSaveFailed: { abortable: false },
	onBeforeLoad: { abortable: true },
	onDataLoaded: { abortable: false },
	onDataLoading: { abortable: false },
	onMasterBeforeLoad: { abortable: true },
	onPartialDataLoaded: { abortable: false },
	onDataLoadFailed: { abortable: false },
	onDirtyChanged: { abortable: false },
	onFieldChanged: { abortable: false },
	onParameterUpdated: { abortable: false },
	onRecordCreated: { abortable: false },
	onRecordRefreshed: { abortable: false },
	onRecordDeleting: { abortable: true },
	onRecordDeleted: { abortable: false },
	onRecordDeleted2: { abortable: false },
	onRowCountLoaded: { abortable: false },
	onClearFilter: { abortable: false },
};

type CustomEventify<T> = { [K in keyof T]: CustomEvent<T[K]> };

export class EventTarget<Events extends Record<string, unknown>> {
	readonly eventHandler: Emitter<CustomEventify<Events>>;
	protected events: Record<keyof Events, { abortable: boolean }>;

	constructor(events: Record<keyof Events, { abortable: boolean }>) {
		this.eventHandler = mitt();
		this.events = events;
	}

	/**
	 * This maps the handler functions to a function that detaches the event.
	 * This way, we can still detach the handler by reference if we have actually
	 * attached a wrapped handler.
	 */
	private detachFunctionMap = new Map<Handler<any>, Handler<any>>();

	attachEvent = <K extends keyof Events>(
		event: K,
		func: Handler<CustomEventify<Events>[K]>,
		options: EventOptions = {},
	): (() => void) => {
		let handler: Handler<CustomEventify<Events>[K]> = func;
		if (options.once === true) {
			handler = (data: CustomEventify<Events>[K]) => {
				func(data);
				this.detachEvent(event, func);
			};
		}

		this.detachFunctionMap.set(func, handler);
		this.eventHandler.on(event, handler);

		const { signal } = options;
		if (signal) {
			const onAbort = () => {
				this.detachEvent(event, func);
			};
			signal.addEventListener("abort", onAbort, { once: true });
		}

		return () => this.detachEvent(event, handler);
	};

	detachEvent = <K extends keyof Events>(event: K, func: Handler<CustomEventify<Events>[K]>): void => {
		let handler = this.detachFunctionMap.get(func);
		if (handler) {
			this.eventHandler.off(event, handler);
			this.detachFunctionMap.delete(func);
		}
	};

	protected fireEvent = <K extends keyof Events>(eventName: K, args?: Events[K]) => {
		let event = new globalThis.CustomEvent(eventName as string, {
			cancelable: this.events[eventName].abortable,
			detail: args,
		});
		this.eventHandler.emit(eventName, event);

		return !event.defaultPrevented;
	};
}
